import React from 'react';
import { ShowcaseItem as ShowcaseItemType } from '../../../types';
import { getShowcaseImageUrl } from '../../../utils/showcase';
import { motion, HTMLMotionProps } from 'framer-motion';

interface ShowcaseItemProps extends HTMLMotionProps<"div"> {
  item: ShowcaseItemType;
  isOwnProfile: boolean;
  layoutType: 'grid' | 'canvas';
  isEditing?: boolean;
  children?: React.ReactNode;
}

const ShowcaseItem: React.FC<ShowcaseItemProps> = ({ 
  item, 
  isOwnProfile, 
  layoutType, 
  isEditing = false,
  children,
  ...props
}) => {
  const { x, y, w, h, rotation, intensity, border_radius, border_type, direction = 'bottom', image_url, medium_url } = item;

  const getBorderStyle = (): React.CSSProperties => {
    const alpha = (intensity || 50) / 100;
    const dirMap: { [key: string]: string } = {
      top: 'to top',
      bottom: 'to bottom',
      left: 'to left',
      right: 'to right'
    };
    const dir = dirMap[direction] || 'to bottom';

    switch (border_type) {
      case 'solid':
        return { 
          border: `${(intensity || 50) / 10}px solid rgba(255, 255, 255, ${alpha})`,
          boxShadow: `0 0 ${(intensity || 50) / 2}px rgba(0,0,0,0.1)`
        };
      case 'fading-soft':
        return { 
          maskImage: `linear-gradient(${dir}, black ${100 - (intensity || 50)}%, transparent 100%)`,
          WebkitMaskImage: `linear-gradient(${dir}, black ${100 - (intensity || 50)}%, transparent 100%)`
        };
      case 'fading-blended':
        return { 
          maskImage: `linear-gradient(${dir}, black 0%, transparent ${100 - (intensity || 50)}%)`,
          WebkitMaskImage: `linear-gradient(${dir}, black 0%, transparent ${100 - (intensity || 50)}%)`
        };
      default:
        return {};
    }
  };

  const style: React.CSSProperties = layoutType === 'canvas' 
    ? {
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: `${w}px`,
        height: `${h}px`,
        zIndex: item.order,
      }
    : {
        gridColumn: `span ${w} / span ${w}`,
        gridRow: `span ${h} / span ${h}`,
        gridColumnStart: x + 1,
        gridRowStart: y + 1,
        aspectRatio: `${w} / ${h}`,
        zIndex: item.order,
      };

  return (
    <motion.div
      layout={false}
      initial={{ opacity: 0, scale: 0.9, rotate: rotation || 0 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        rotate: rotation || 0
      }}
      transition={
        isEditing 
          ? { rotate: { duration: 0.2 } }
          : { duration: 0 }
      }
      className={`group ${layoutType === 'canvas' ? 'absolute' : 'relative'} ${layoutType === 'grid' ? 'shadow-sm' : ''} ${isEditing ? 'cursor-move' : ''} ${props.className || ''}`}
      style={style}
      {...props}
    >
      <div 
        className="w-full h-full relative overflow-hidden"
        style={{ borderRadius: `${border_radius !== undefined ? border_radius : 16}px` }}
      >
        <img
          src={getShowcaseImageUrl(medium_url || image_url)}
          alt=""
          className={`w-full h-full object-cover transition-transform duration-700 ${!isEditing ? 'group-hover:scale-110' : ''}`}
          style={getBorderStyle()}
        />
      </div>

      {children}
    </motion.div>
  );
};

export default ShowcaseItem;
